import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { reportGroupEditStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupEditStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { Form } from './Form';
import { validate } from './validateSchema';

export const ReportGroupEdit = observer(() => {
  const { code } = useParams<{ code: string }>();
  const { push } = useHistory();
  const { setNew, fetch, setEditMode, reportGroup, save, isNew, isEditMode } = reportGroupEditStore;
  const { state } = useLocation();

  const permission = usePermission('CmReportGroup', reportGroupEditStore);

  useEffect(() => {
    fetchReferences(['reportGroups']);
  }, []);

  useEffect(() => {
    if (code !== NEW_ENTITY_ID) {
      fetch(code);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, setNew, setEditMode, code]);

  const initialValues = useMemo(() => {
    if (state && state.isCopy) {
      return { ...state.isCopy };
    }

    return reportGroup ? { ...reportGroup, id: reportGroup?.code } : {};
  }, [state, reportGroup]); // id нужен для проверки на уникальность кода

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const value = await save({ ...values });

        if (isNew && value?.code) {
          push(`${RoutePath.DICTIONARY_REPORT_GROUPS}/${value.code}`);
        }
      })();

      return undefined;
    },
    [isNew, permission.CREATE, permission.UPDATE, push, save],
  );

  return (
    <DesignLayout title="Справочник: Группы отчетности" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <Form />
      </FormWrapper>
    </DesignLayout>
  );
});
